import Rollbar from 'rollbar';

// Parcel 1 inlines `process.env.*` at build time, so a var missing from the active
// .env file ends up as a literal `undefined` in the bundle — never as a throw.
const accessToken = process.env.ROLLBAR_ACCESS_TOKEN;

// With no token every report would still be POSTed and rejected by the API, so keep
// the notifier fully off instead. `enabled: false` is checked in the notifier before
// anything hits the network, which is what local dev (no token) runs with.
const enabled = Boolean(accessToken);

// @rollbar/react's Provider rejects an `instance` whose options carry no accessToken
// (its isRollbarInstance check), so give the disabled notifier a placeholder rather
// than letting the invariant crash the app at boot.
const DISABLED_TOKEN = 'rollbar-disabled';

const rollbarConfig = {
    accessToken: accessToken || DISABLED_TOKEN,
    enabled,
    environment: process.env.ROLLBAR_ENVIRONMENT || process.env.NODE_ENV || 'development',
    captureUncaught: true,
    captureUnhandledRejections: true,
    // Landlords are identified by id/name/email in the person payload already; the
    // raw IP adds nothing and is personal data we don't need to ship to a US vendor.
    captureIp: 'anonymize',
    ignoredMessages: [
        // Fires on ordinary layout thrash in Chrome. Never actionable.
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Cross-origin scripts (Google Maps, Stripe Connect, browser extensions)
        // are reported with no usable frames, so there is nothing to fix.
        'Script error'
    ],
    // Appended to Rollbar's built-in list (password, cc-*, …), not a replacement.
    scrubFields: ['iban', 'nif', 'fiscalName', 'fiscal_name', 'vat', 'apiKey', 'api_key'],
    payload: {
        client: {
            javascript: {
                source_map_enabled: true,
                guess_uncaught_frames: true
            }
        }
    }
};

// Rollbar.init keeps a module-level singleton, so any later init (ours or a
// dependency's) reconfigures this notifier instead of standing up a second one.
const rollbar = Rollbar.init(rollbarConfig);

// Rollbar swallows its own transport failures, but a bad payload here (a getter that
// throws, a circular custom object) would otherwise take down the call site.
const safely = (fn) => {
    try {
        return fn();
    } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('rollbar reporting failed', e);
        return undefined;
    }
};

/**
 * Attach the signed-in landlord to every subsequent item. Pass a falsy user to clear
 * it on logout so a shared browser doesn't keep reporting under the previous account.
 */
const setRollbarPerson = (user) => safely(() => {
    if (!user || (!user.id && user.id !== 0)) {
        return rollbar.configure({payload: {person: null}});
    }
    return rollbar.configure({
        payload: {
            person: {
                id: String(user.id),
                username: user.name,
                email: user.email
            }
        }
    });
});

/**
 * Merge extra keys into the payload sent with every item — used for the tenant the
 * app booted into, which decides which theme/functionalities are live.
 */
const setRollbarPayload = (payload) => safely(() => rollbar.configure({payload}));

/**
 * Report without caring whether Rollbar is enabled or whether the argument is a real
 * Error — `level` is one of debug/info/warning/error/critical.
 */
const reportError = (level, message, error, custom) =>
    safely(() => rollbar[level](message, error, custom));

export {rollbar, rollbarConfig, setRollbarPerson, setRollbarPayload, reportError};
export default rollbar;
